import React, { useState } from "react";
import { Search, Package, CheckCircle2, Clock, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

const STAGES = ["AWAITING_PAYMENT", "PAID", "MEASUREMENTS_REVIEW", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "DELIVERED"];
const STAGE_LABEL = {
  ar: { AWAITING_PAYMENT: "بانتظار الدفع", PAID: "تم الدفع", MEASUREMENTS_REVIEW: "مراجعة المقاسات", IN_PRODUCTION: "قيد التفصيل", READY: "جاهز", OUT_FOR_DELIVERY: "خرج للتوصيل", DELIVERED: "تم التسليم" },
  en: { AWAITING_PAYMENT: "Awaiting payment", PAID: "Paid", MEASUREMENTS_REVIEW: "Measurements review", IN_PRODUCTION: "In production", READY: "Ready", OUT_FOR_DELIVERY: "Out for delivery", DELIVERED: "Delivered" },
};

function Timeline({ status, cancelled, lang }) {
  const L = STAGE_LABEL[lang];
  if (cancelled) {
    return (
      <div className="flex items-center gap-2 text-destructive text-sm mt-3" data-testid="track-cancelled">
        <XCircle className="w-5 h-5" /> {lang === "ar" ? "هذا الطلب ملغي / مسترد" : "This order is cancelled / refunded"}
      </div>
    );
  }
  const idx = STAGES.indexOf(status);
  return (
    <div className="mt-4 space-y-0" data-testid="track-timeline">
      {STAGES.map((s, i) => {
        const done = idx >= 0 && i <= idx;
        const active = i === idx;
        return (
          <div key={s} className="flex items-center gap-3 py-1.5">
            {done ? <CheckCircle2 className="w-5 h-5 text-[#6B563F]" /> : <Clock className="w-5 h-5 text-[#141110]/25" />}
            <span className={`text-sm ${active ? "font-semibold text-[#141110]" : done ? "text-[#141110]/80" : "text-[#141110]/40"}`}>{L[s]}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function TrackOrder() {
  const { lang } = useLang();
  const ar = lang === "ar";
  const [mode, setMode] = useState("ref");
  const [val, setVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!val.trim()) { setError(mode === "ref" ? (ar ? "أدخلي رقم الطلب" : "Enter the order number") : (ar ? "أدخلي رقم الهاتف" : "Enter the phone number")); return; }
    setLoading(true); setError(""); setOrders(null);
    try {
      const params = mode === "ref" ? { order_ref: val.trim() } : { phone: val.trim() };
      const { data } = await api.get("/orders/track", { params });
      setOrders(data.orders || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || (ar ? "تعذّر البحث" : "Search failed"));
    } finally { setLoading(false); }
  };

  const inputCls = "w-full border border-border px-4 py-3 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 py-8 md:py-14 fade-in">
      <div className="text-center mb-8">
        <Package className="w-8 h-8 mx-auto text-accent mb-3" strokeWidth={1.3} />
        <h1 className="font-display text-3xl md:text-4xl">{ar ? "تتبع الطلب" : "Track Order"}</h1>
        <p className="text-text-muted text-sm mt-2">{ar ? "تابعي حالة طلبك برقم الطلب أو رقم الهاتف" : "Track your order status by order number or phone"}</p>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => { setMode("ref"); setError(""); }} data-testid="track-mode-ref"
          className={`flex-1 py-2.5 text-sm border transition-colors ${mode === "ref" ? "bg-[#141110] text-white border-[#141110]" : "bg-white border-border"}`}>{ar ? "رقم الطلب" : "Order number"}</button>
        <button onClick={() => { setMode("phone"); setError(""); }} data-testid="track-mode-phone"
          className={`flex-1 py-2.5 text-sm border transition-colors ${mode === "phone" ? "bg-[#141110] text-white border-[#141110]" : "bg-white border-border"}`}>{ar ? "رقم الهاتف" : "Phone number"}</button>
      </div>

      <form onSubmit={submit} className="flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} data-testid="track-input"
          dir="ltr" placeholder={mode === "ref" ? "RNF-ORD-2026-000001" : "+965…"} className={inputCls} />
        <button type="submit" disabled={loading} data-testid="track-submit"
          className="btn-primary px-5 py-3 text-sm shrink-0 flex items-center gap-1"><Search className="w-4 h-4" /> {loading ? "…" : (ar ? "بحث" : "Search")}</button>
      </form>
      {error && <p className="text-sm text-destructive mt-3" data-testid="track-error">{error}</p>}

      {orders && orders.length === 0 && <p className="text-text-muted text-sm mt-6 text-center">{ar ? "لا توجد نتائج" : "No results"}</p>}
      {orders && orders.map((o) => (
        <div key={o.order_ref} className="bg-white border border-border p-5 mt-6" data-testid="track-result">
          <div className="flex items-center justify-between">
            <span className="font-num text-sm text-accent" dir="ltr">{o.order_ref}</span>
            <span className="text-xs bg-surface px-2 py-1">{STAGE_LABEL[lang][o.status] || o.status_label}</span>
          </div>
          <div className="text-xs text-text-muted mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <span>{ar ? "العميلة" : "Customer"}: {o.customer_name || "—"}</span>
            <span>{o.delivery_type}</span>
            <span>{ar ? "عدد القطع" : "Items"}: {o.items_count}</span>
            <span dir="ltr">{formatKWD(o.total, lang)}</span>
          </div>
          <Timeline status={o.status} cancelled={o.cancelled} lang={lang} />
        </div>
      ))}
    </div>
  );
}
